/**
 * @fileoverview Where a Google Photos pick that outlived its dialog goes (B40).
 *
 * The pick keeps its target (PickTarget: the screen it started from and that
 * screen's ids) in the pending record, which lives in localStorage as plumbing
 * until the photos are in. When the photos come and the dialog is gone, this
 * says which route they take: onto the plant, onto the bed, into the packet
 * reader, to Today's picture (its dialog, to confirm), to the profile photo,
 * over a photo being replaced, or to the Photos tray (a
 * pick started there, or one with nowhere particular to go). Only the tray
 * runs the AI guess, and only because the gardener started there.
 *
 * Pure: no browser, no imports but a type, so it runs under plain node.
 *
 * Where: GooglePhotosResume decides the delivery with it.
 * API: none.
 *
 * @module googlePhotosTarget
 */

import type { PickTarget } from './googlePhotosFlow.ts';

/** The ways a late pick is delivered. */
export type LateRoute = 'plant' | 'bed' | 'packet' | 'today' | 'profile' | 'replace' | 'tray';

/** A late pick's route and the ids it needs. */
export interface LateDelivery {
  /** Which way the photos go. */
  route: LateRoute;
  /** The plant's plant_obj_id, the bed's id, or (for a replace) the photo's id; else null. */
  id: number | null;
  /** For a replace: the plant whose photo it is, or null when it is a bed's. */
  plantId: number | null;
  /** The bed, for the packet reader and the tray; else null. */
  locationId: number | null;
}

/** Routes that take one photo only: a packet label, Today's picture, the profile photo, a replacement. */
const SINGLE_PHOTO_ROUTES: ReadonlySet<LateRoute> = new Set<LateRoute>(['packet', 'today', 'profile', 'replace']);

/**
 * Whether a number is a usable id.
 *
 * @param value - Whatever the pending record held.
 * @returns True for a positive whole number.
 */
function isId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * Which way a late pick goes, from the target it kept. A plant or bed target
 * without its id cannot reach its screen, so it falls back to the tray, as a
 * pick with no target always has.
 *
 * @param target - The pick's target, or undefined for an old record.
 * @returns The route, with the ids it needs.
 */
export function lateDelivery(target: PickTarget | undefined | null): LateDelivery {
  const kind = target?.kind ?? 'other';
  const locationId = isId(target?.locationId) ? target.locationId : null;
  const plantId = isId(target?.plantId) ? target.plantId : null;
  if (kind === 'replace' && isId(target?.id) && (plantId !== null || locationId !== null)) {
    return { route: 'replace', id: target.id, plantId, locationId: plantId === null ? locationId : null };
  }
  if (kind === 'plant' && isId(target?.id)) return { route: 'plant', id: target.id, plantId: null, locationId };
  if (kind === 'bed') {
    const id = isId(target?.id) ? target.id : locationId;
    if (id !== null) return { route: 'bed', id, plantId: null, locationId: id };
  }
  if (kind === 'packet') return { route: 'packet', id: null, plantId: null, locationId };
  if (kind === 'today') return { route: 'today', id: null, plantId: null, locationId: null };
  if (kind === 'profile') return { route: 'profile', id: null, plantId: null, locationId: null };
  return { route: 'tray', id: null, plantId: null, locationId };
}

/**
 * The photos a route takes: all of them for a plant, a bed and the tray, the
 * first only for a packet label, Today's picture and the profile photo.
 *
 * @param route - The route.
 * @param picks - Everything the gardener picked.
 * @returns The photos to deliver.
 */
export function photosForRoute<T>(route: LateRoute, picks: readonly T[]): T[] {
  return SINGLE_PHOTO_ROUTES.has(route) ? picks.slice(0, 1) : [...picks];
}

/**
 * Whether a route changes what the plants and beds lists show (a photo put on
 * a plant or a bed, or swapped on one), so the app reads exactly those two
 * again and nothing else. The tray reads its own; Today's dialog and the
 * packet review refresh what they change; the profile photo is the account's.
 *
 * @param route - The route.
 * @returns True for plant, bed and replace.
 */
export function refreshesGardenLists(route: LateRoute): boolean {
  return route === 'plant' || route === 'bed' || route === 'replace';
}

/**
 * Whether a route runs the AI guess on the photos it takes in. Only the tray
 * does (when the Photos page next opens); every other route has an answer
 * from the gardener already.
 *
 * @param route - The route.
 * @returns True for the tray.
 */
export function routeIsGuessed(route: LateRoute): boolean {
  return route === 'tray';
}
